import { Injectable } from '@angular/core';
import {
  IJobsiteSalesAreasRequest,
  IJobsiteSalesAreasResponse,
  Jobsite,
  JobsiteCommercialChannelService,
  JobsiteSalesAreasService,
  OrderTypeCodesEnum,
  PROJECT_TYPE_CODE,
  StdLotSizeService,
} from '@cemex-core/cmx-api';
import { IJobsiteCommercialChannelRequest } from '@cemex-core/cmx-api/models/jobsite-commercial-channel/jobsite-commercial-channel-request';
import { IJobsiteSalesArea } from '@cemex-core/cmx-api/models/jobsite-sales-areas/jobsite-sales-area';
import { LotCapacity } from '@cemex-core/cmx-api/models/jobsite-std-lot/lot-capacity';
import { ILegalEntity } from '@cemex-core/cmx-api/models/legal-entities/legal-entity';
import { StdLotSizeReq, StdLotSizeRes } from '@cemex-core/cmx-api/models/std-lot-size';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, tap, debounceTime } from 'rxjs/operators';
import { DIVISION_CODE } from '../../shared/constants/division-codes';
import { SHIPMENT_LOCATION_TYPE_ID } from '../../shared/constants/shipment-location-type-id';
import { divisionSalesArea } from '../../shared/utils/sales-area-by-division.method';
import {
  OneClickJobsitesActionTypes,
  OneClickLoadJobsitesAction,
  OneClickLoadJobsiteSalesAreaCompleteAction,
  OneClickLoadJobsitesCompleteAction,
  OneClickLoadJobsiteStdLotCompletedAction,
  OneClickPreLoadJobsitesAction,
} from '../actions/one-click-jobsites.actions';
import { OneClickSelectQuantityAction } from '../actions/one-click-quantity.actions';
import {
  OneClickSelectShippingModeAction,
  OneClickShippingModeActionTypes,
} from '../actions/one-click-shipment-mode.actions';
import { ShippingLocationModeCode } from '../models/shipping-location-mode.model';
import { State } from '../reducers';
import { getOneClickBuilderEditMode, getOneClickPreloadedOrder } from '../selectors/one-click-profiles.selector';
import { getOneClickState, OneClickState } from './../reducers/index';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { getCustomerAndOneClickState } from '@ordersnproducts/modules/one-click-order/selectors/one-click-customer.selector';
import { BusinessLine } from '@cemex-core/cmx-api/models/business-lines/business-line';
import { PRODUCT_LINE_CODE } from '@ordersnproducts/modules/shared/constants/product-lines-codes';
import { SHIPPING_CONDITION_CODE } from '@ordersnproducts/modules/shared/constants/shipping-conditions-codes';
import { IJobsiteCommercialChannelResponse } from '@cemex-core/cmx-api/models/jobsite-commercial-channel/get-jobsite-commercial-channel-response';

@Injectable()
export class OneClickJobsitesEffects {
  selectLocationMode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OneClickShippingModeActionTypes.SELECT),
        switchMap((action: OneClickSelectShippingModeAction) =>
          this.store.pipe(
            select(getOneClickPreloadedOrder),
            debounceTime(300),
            take(1),
            map((preloadedOrder) => {
              return { preloadedOrder, action };
            }),
          ),
        ),
        map(({ preloadedOrder, action }) => {
          if (action.payload.code === ShippingLocationModeCode.Jobsite) {
            if (preloadedOrder) {
              this.store.dispatch(new OneClickPreLoadJobsitesAction(preloadedOrder));
            } else {
              this.store.dispatch(new OneClickLoadJobsitesAction());
            }
          }
        }),
      ),
    { dispatch: false },
  );

  loadJobsites$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(OneClickJobsitesActionTypes.LOAD_JOBSITES),
      switchMap(() => this.store.pipe(select(getCustomerAndOneClickState), take(1))),
      map((customerAndState) =>
        this.makeJobsitesRequest(customerAndState.legalEntity, customerAndState.state.businessLinesState.selected),
      ),
      switchMap((request: IJobsiteCommercialChannelRequest) =>
        this.jobsiteCommercialChannelService.getJobsiteByCommercialChannelCodeV7(request),
      ),
      map((response: IJobsiteCommercialChannelResponse) => new OneClickLoadJobsitesCompleteAction(response.projects)),
    ),
  );

  preloadJobsites$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(OneClickJobsitesActionTypes.PRELOAD_JOBSITE),
      switchMap((action: OneClickPreLoadJobsitesAction) =>
        this.store.pipe(
          select(getLegalEntity),
          take(1),
          map((customer) => this.makeJobsiteFromPreload(action.payload.jobsite, customer)),
        ),
      ),
      map((project) => new OneClickLoadJobsitesCompleteAction([project])),
    ),
  );

  stdLotSizeLoad = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OneClickJobsitesActionTypes.LOAD_STD_LOT),
        switchMap(() =>
          this.store.select(getOneClickState).pipe(
            filter((state) => !!state.jobsiteState.selected),
            take(1),
          ),
        ),
        switchMap((state) =>
          this.loadSalesAreas(
            state.jobsiteState.selected.jobsite.jobsiteId,
            Number(
              state.businessLinesState.selected
                ? state.businessLinesState.selected.productLineCode
                : state.profileState.presetOrder.items[0].product.productType.productTypeCode,
            ),
          ).pipe(
            tap((response: IJobsiteSalesAreasResponse) => this.store.dispatch(this.completeSalesAreaAction(response))),
            map((response: IJobsiteSalesAreasResponse) => this.makeStdLotRequest(state, response.jobsiteSalesAreas)),
          ),
        ),
        filter((request) => !!request),
        switchMap((request: StdLotSizeReq) => this.stdLotSizeService.getStdLotSize(request)),
        map((response: StdLotSizeRes) => response && response.capacities[0]),
        filter((capactity) => !!capactity),
        tap((capacity) => this.presetQuantityIfNeeded(capacity)),
        tap((capacity) => {
          this.store.dispatch(new OneClickLoadJobsiteStdLotCompletedAction(capacity));
        }),
      ),
    { dispatch: false },
  );

  oneClickJobsiteSalesArea$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(OneClickJobsitesActionTypes.LOAD_JOBSITES_SALES_AREA),
      switchMap(() => this.store.select(getOneClickState).pipe(take(1))),
      filter((state) => !!state.jobsiteState.selected),
      map((state) => this.makeSalesAreasRequestFromState(state)),
      switchMap((request: IJobsiteSalesAreasRequest) => this.jobsiteSalesAreaService.getAddresses(request)),
      map((response: IJobsiteSalesAreasResponse) => this.completeSalesAreaAction(response)),
    ),
  );

  private presetQuantityIfNeeded(capacity: LotCapacity) {
    this.store
      .select(getOneClickBuilderEditMode)
      .pipe(take(1))
      .subscribe((isEdit) => {
        if (!isEdit) {
          this.store.dispatch(new OneClickSelectQuantityAction(capacity.lotMax, 0));
        }
      });
  }

  private makeJobsiteFromPreload(jobsite: Jobsite, customer: ILegalEntity) {
    return {
      customer: {
        customerCode: customer.legalEntityCode,
        customerDesc: customer.legalEntityDesc,
        customerId: customer.legalEntityId,
      },
      jobsite: {
        jobsiteId: jobsite.jobsiteId,
        jobsiteDesc: jobsite.jobsiteDesc,
        jobsiteCode: jobsite.jobsiteCode,
      },
    };
  }

  private completeSalesAreaAction(response: IJobsiteSalesAreasResponse): OneClickLoadJobsiteSalesAreaCompleteAction {
    return new OneClickLoadJobsiteSalesAreaCompleteAction(
      divisionSalesArea(response.jobsiteSalesAreas, DIVISION_CODE.CEMENT) as IJobsiteSalesArea,
    );
  }

  private makeStdLotRequest(state: OneClickState, salesAreas: IJobsiteSalesArea[]): StdLotSizeReq {
    if (!this.shouldCallStdLot(state)) {
      return null;
    }
    return {
      jobsiteId: state.jobsiteState.selected.jobsite.jobsiteId,
      productLineCode: state.businessLinesState.selected
        ? state.businessLinesState.selected.productLineCode
        : state.profileState.presetOrder.items[0].product.productType.productTypeCode,
      salesAreaId: divisionSalesArea(salesAreas, DIVISION_CODE.CEMENT).salesArea.salesAreaId,
      ...(state.pointOfDeliveriesState.selected && {
        pointOfDeliveryId: state.pointOfDeliveriesState.selected.shippingDestinationId,
      }),
    } as StdLotSizeReq;
  }

  private loadSalesAreas(shipmentLocationId: any, businessLineId: number) {
    return this.jobsiteSalesAreaService.getAddresses({
      shipmentLocationId: {
        entityId: shipmentLocationId,
        entityTypeId: SHIPMENT_LOCATION_TYPE_ID.JOBSITE,
      },
      productLineId: businessLineId,
    });
  }

  private makeJobsitesRequest(legalEntity: ILegalEntity, businessLine: BusinessLine): IJobsiteCommercialChannelRequest {
    return {
      projectTypeCode: PROJECT_TYPE_CODE.JOBSITE,
      productLineCode: businessLine.productLineCode,
      commercialChannelCode: OrderTypeCodesEnum.OneClickToBuy,
      customerCode: legalEntity.legalEntityTypeCode,
    } as IJobsiteCommercialChannelRequest;
  }

  private makeSalesAreasRequestFromState(state: OneClickState): IJobsiteSalesAreasRequest {
    return {
      shipmentLocationId: {
        entityId: state.jobsiteState.selected.jobsite.jobsiteId,
        entityTypeId: SHIPMENT_LOCATION_TYPE_ID.JOBSITE,
      },
      productLineId: state.businessLinesState.selected
        ? state.businessLinesState.selected.productLineId
        : state.profileState.presetOrder.items[0].product.productType.productTypeId,
    } as IJobsiteSalesAreasRequest;
  }

  private shouldCallStdLot(state): boolean {
    return (
      !state.businessLinesState.selected ||
      (state.businessLinesState.selected.productLineCode === PRODUCT_LINE_CODE.BAG_CEMENT &&
        state.shippingMethodsState.selected.shippingConditionCode === SHIPPING_CONDITION_CODE.DELIVERY)
    );
  }

  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private stdLotSizeService: StdLotSizeService,
    private jobsiteSalesAreaService: JobsiteSalesAreasService,
    private jobsiteCommercialChannelService: JobsiteCommercialChannelService,
  ) {}
}
